import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { useScrollProgress } from '../hooks/useScrollProgress';
import { useChatContext } from '../context/ChatContext';
import Logo from './Logo';

const navItems = [
  { label: 'Leistungen', href: '#leistungen' },
  { label: 'Ablauf', href: '#ablauf' },
  { label: 'Referenzen', href: '#referenzen' },
  { label: 'Videos', href: '#videos' },
  { label: 'Assistent', href: '#assistent' },
  { label: 'Kontakt', href: '#kontakt' },
];

function Header() {
  const { scrolled } = useScrollProgress();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const { openChat } = useChatContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    if (!isHome) {
      setActiveSection('');
      return;
    }

    const handleScroll = () => {
      const marker = window.scrollY + Math.min(window.innerHeight * 0.42, 420);
      let current = '';
      navItems.forEach(({ href }) => {
        const id = href.slice(1);
        const section = document.getElementById(id);
        if (section && section.getBoundingClientRect().top + window.scrollY <= marker) {
          current = href;
        }
      });
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  useEffect(() => {
    if (mobileOpen) {
      document.documentElement.classList.add('is-menu-open');
    } else {
      document.documentElement.classList.remove('is-menu-open');
    }
  }, [mobileOpen]);

  const handleNavClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      if (!isHome) {
        navigate('/');
        setTimeout(() => {
          const id = href.slice(1);
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
          if (id === 'assistent') {
            openChat();
          }
        }, 100);
        setMobileOpen(false);
        return;
      }
      const id = href.slice(1);
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      if (id === 'assistent') {
        openChat();
      }
      setMobileOpen(false);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          scrolled
            ? 'bg-gradient-to-br from-[rgba(6,47,56,0.84)] to-[rgba(7,74,87,0.76)] text-white shadow-[0_16px_42px_rgba(5,39,47,0.2)] backdrop-blur-xl border-b border-signal/20'
            : 'bg-transparent text-white'
        } ${
          mobileOpen
            ? 'bg-gradient-to-br from-[rgba(6,47,56,0.94)] to-[rgba(7,74,87,0.9)] rounded-none border-0'
            : ''
        }`}
        style={{ top: scrolled ? 0 : 'clamp(0.6rem, 1.4vw, 1rem)' }}
      >
        <div className="container-site mx-auto flex items-center justify-between min-h-[64px] py-2">
          <NavLink to="/" className="block w-[clamp(178px,15.5vw,212px)] min-h-[44px]">
            <Logo />
          </NavLink>

          <nav className="hidden lg:flex items-center gap-1 px-2 py-1 rounded-[14px] border border-signal-light/20 bg-gradient-to-br from-[rgba(6,47,56,0.92)] to-[rgba(7,74,87,0.86)] backdrop-blur-xl shadow-[0_18px_42px_rgba(3,15,31,0.24)]">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                aria-current={activeSection === item.href ? 'true' : undefined}
                className={`relative flex items-center justify-center min-h-[40px] px-4 py-2 rounded-lg border border-signal/20 bg-gradient-to-b from-signal/15 to-signal/5 text-white/90 font-semibold text-sm transition-all hover:bg-gradient-to-b hover:from-signal/20 hover:to-signal-light/10 hover:border-signal/40 hover:-translate-y-0.5 ${
                  activeSection === item.href
                    ? 'bg-gradient-to-br from-signal to-signal-light text-[#071827] border-transparent'
                    : ''
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="tel:+436609091992"
              className="hidden md:inline-flex items-center gap-2 min-h-11 px-4 rounded-full bg-white/90 text-ink font-bold border border-signal-light/60 shadow-[0_12px_30px_rgba(0,0,0,0.13)] backdrop-blur-sm hover:bg-white transition-colors"
            >
              <Phone className="w-4 h-4 text-signal" />
              +43 660 909 1992
            </a>

            <button
              className="lg:hidden w-12 h-11 rounded-[11px] border border-signal/35 bg-gradient-to-br from-[rgba(6,47,56,0.74)] to-signal/25 text-white shadow-[0_14px_28px_rgba(3,15,31,0.18)] flex items-center justify-center"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? 'Menü schließen' : 'Menü öffnen'}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 bg-[rgba(5,18,24,0.55)] backdrop-blur-md transition-opacity duration-500 ease-out ${
          mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileOpen(false)}
        aria-hidden={!mobileOpen}
      />

      <div
        className={`fixed top-0 right-0 bottom-0 z-[51] w-[min(420px,100vw)] overflow-hidden transform transition-transform duration-[480ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          background: 'linear-gradient(180deg, rgba(12,36,44,0.92) 0%, rgba(10,30,38,0.95) 40%, rgba(8,24,32,0.97) 100%)',
          backdropFilter: 'blur(40px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(40px) saturate(1.4)',
        }}
        aria-hidden={!mobileOpen}
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{ background: 'radial-gradient(circle at 80% 0%, rgba(50,185,232,0.18), transparent 18rem)' }}
          aria-hidden="true"
        />

        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-[calc(max(1rem,env(safe-area-inset-top))+1.25rem)] right-5 w-11 h-11 rounded-full border border-white/15 bg-white/10 text-white flex items-center justify-center hover:bg-white/20 hover:border-white/30 transition-all z-10 backdrop-blur-sm"
          aria-label="Menü schließen"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col h-full pt-[calc(max(1rem,env(safe-area-inset-top))+5.5rem)] px-6 pb-6 relative z-[1]">
          <p className="text-xs font-black uppercase tracking-wider text-signal/80 mb-4 px-1">Navigation</p>
          <nav className="flex flex-col gap-2.5">
            {navItems.map((item, index) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                style={{ animationDelay: `${mobileOpen ? index * 60 : 0}ms` }}
                className={`group/link flex items-center justify-between min-h-[56px] px-5 rounded-2xl border text-white font-bold text-base transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] ${
                  activeSection === item.href
                    ? 'border-signal/50 bg-gradient-to-r from-signal/20 to-signal/5 shadow-[0_8px_28px_rgba(50,185,232,0.18)]'
                    : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className={`w-1.5 h-1.5 rounded-full transition-all ${
                    activeSection === item.href ? 'bg-signal shadow-[0_0_10px_rgba(50,185,232,0.6)]' : 'bg-white/20 group-hover/link:bg-white/40'
                  }`} />
                  {item.label}
                </span>
                <span className={`w-2 h-2 border-t-2 border-r-2 rotate-45 transition-all ${
                  activeSection === item.href ? 'border-signal' : 'border-white/25 group-hover/link:border-white/50'
                }`} aria-hidden="true" />
              </a>
            ))}
          </nav>

          <div className="mt-auto pt-6">
            <div className="flex flex-col gap-3">
              <a
                href="tel:+436609091992"
                className="flex items-center justify-center gap-2 w-full min-h-[52px] rounded-full bg-gradient-to-r from-signal to-[#63d0f3] text-ink font-extrabold border border-white/20 shadow-[0_12px_36px_rgba(50,185,232,0.28)] hover:shadow-[0_16px_44px_rgba(50,185,232,0.36)] hover:-translate-y-0.5 transition-all active:scale-[0.98]"
              >
                <Phone className="w-4 h-4" />
                +43 660 909 1992
              </a>
              <p className="text-center text-xs text-white/40 font-medium pt-1">Direkter Draht · Mo–Sa</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Header;
